import { Bell, Inbox } from "lucide-react";

import { TenkoIconButton } from "@/shared/components/buttons/TenkoIconButton";
import { TenkoBadge } from "@/shared/components/data-display/TenkoBadge";
import { TenkoEmpty } from "@/shared/components/feedback/TenkoEmpty";
import { TenkoPopover } from "@/shared/components/overlays/TenkoPopover";
import { cn } from "@/shared/lib/utils";

export interface AppNotification {
  id: string;
  title: string;
  summary?: string;
  read_at: string | null;
}

export type AppShellNotificationsBellLabels = {
  title: string;
  emptyTitle: string;
};

export type TenkoAppShellNotificationsBellProps = {
  labels: AppShellNotificationsBellLabels;
  items: AppNotification[];
  unreadCount: number;
  onMarkAsRead: (id: string) => void;
};

/** Presentational header bell; the app decides where notifications come from. */
export function TenkoAppShellNotificationsBell({
  labels,
  items,
  unreadCount,
  onMarkAsRead,
}: TenkoAppShellNotificationsBellProps) {
  return (
    <div className="relative inline-flex">
      <TenkoPopover
        trigger={<TenkoIconButton icon={Bell} className="rounded-full" />}
        title={labels.title}
        contentClassName="min-w-md max-w-md"
        align="end"
        sideOffset={12}
      >
        {items.length === 0 ? (
          <TenkoEmpty title={labels.emptyTitle} icon={Inbox}>
            <div></div>
          </TenkoEmpty>
        ) : (
          <ul className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            {items.map((notification) => {
              const isUnread = !notification.read_at;
              return (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isUnread) onMarkAsRead(notification.id);
                    }}
                    className={cn(
                      "flex w-full flex-col items-start gap-1 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted",
                      isUnread && "bg-muted/50",
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate font-medium">{notification.title}</span>
                      {isUnread ? (
                        <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden />
                      ) : null}
                    </div>
                    {notification.summary ? (
                      <span className="text-xs text-muted-foreground line-clamp-2">
                        {notification.summary}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </TenkoPopover>
      {unreadCount > 0 ? (
        <TenkoBadge
          shape="circle"
          className="pointer-events-none absolute -top-1 -right-1 size-4 text-[10px] leading-none"
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </TenkoBadge>
      ) : null}
    </div>
  );
}
